import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import Navigation from "../components/Navigation";
import { useAlert } from "../contexts/AlertContext";
import { getAccessToken } from "../utils/auth";

export interface Location {
  id: string;
  user_id: string;
  location_taken: string;
  deleted: boolean;
  number_downloads: number;
  created_at: string;
  downloads_limit: number;
  downloads_remaining: number;
  is_final_download: boolean;
  is_unlimited: boolean;
}

interface LocationsResponse {
  locations: Location[];
  user_tier: string;
  download_limit: number;
  is_unlimited_tier: boolean;
}

const UploadsPage: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [tierInfo, setTierInfo] = useState<{ tier: string; limit: number; unlimited: boolean } | null>(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState<string | null>(null);
  const navigate = useNavigate();
  const { confirm } = useAlert();

  const fetchLocations = async () => {
    try {
      const res = await fetchWithAuthRetry(
        `https://api.adjusterphoto.com/images/locations`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${getAccessToken() || ""}`,
          },
          credentials: "include",
        },
        navigate,
      );

      if (!res.ok) throw new Error("Failed to fetch locations");

      const data: LocationsResponse = await res.json();
      if (!data || !data.locations) setLocations([]);
      else {
        setLocations(data.locations);
        setTierInfo({
          tier: data.user_tier,
          limit: data.download_limit,
          unlimited: data.is_unlimited_tier,
        });
      }
    } catch (err) {
      console.error(err);
      setError("Could not load locations");
    }
  };

  useEffect(() => {
    fetchLocations();
  }, [navigate]);

  const handleRowClick = (location: string) => {
    navigate(`/location-images?location=${encodeURIComponent(location)}`);
  };

  const handleDownload = async (loc: Location, type: 'structured' | 'unstructured', e: React.MouseEvent) => {
    e.stopPropagation();

    if (loc.downloads_remaining === 0 && !loc.is_unlimited) {
      setError("No downloads remaining for this location.");
      return;
    }

    // Show confirmation dialog before download
    if (loc.is_final_download && !loc.is_unlimited) {
      // Special warning for final download
      const confirmed = await confirm({
        title: 'Final Download Warning',
        message: 'This is your last download for this location. After downloading, the location and all its images will be permanently deleted. Do you want to continue?',
        variant: 'danger',
        confirmLabel: 'Download & Delete',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) return;
    } else {
      // Standard download confirmation
      const downloadType = type === 'structured' ? 'Folders ZIP' : 'Flat ZIP';
      const remainingText = loc.is_unlimited
        ? ''
        : ` You will have ${loc.downloads_remaining - 1} download${loc.downloads_remaining - 1 !== 1 ? 's' : ''} remaining.`;

      const confirmed = await confirm({
        title: 'Confirm Download',
        message: `Download "${loc.location_taken}" as ${downloadType}?${remainingText}`,
        variant: 'info',
        confirmLabel: 'Download',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) return;
    }

    const downloadKey = `${loc.id}-${type}`;
    setDownloading(downloadKey);
    setError("");

    try {
      let url = `https://api.adjusterphoto.com/images/zip-${type}?location=${encodeURIComponent(loc.location_taken)}`;

      if (loc.is_final_download && !loc.is_unlimited) {
        url += "&confirm_delete=true";
      }

      const res = await fetchWithAuthRetry(
        url,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${getAccessToken() || ""}`,
          },
          credentials: "include",
        },
        navigate,
      );

      if (!res.ok) {
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await res.json();
          throw new Error(errorData.message || "Download failed");
        }
        throw new Error("Download failed");
      }

      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `${loc.location_taken}-${type}-images.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      await fetchLocations();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const getDownloadsBadge = (loc: Location) => {
    if (loc.is_unlimited) {
      return <span className="badge badge-success">Unlimited</span>;
    }
    if (loc.downloads_remaining === 0) {
      return <span className="badge badge-danger">No downloads left</span>;
    }
    if (loc.is_final_download) {
      return <span className="badge badge-warning">1 download left (final)</span>;
    }
    return (
      <span className="badge badge-info">
        {loc.downloads_remaining} of {loc.downloads_limit} remaining
      </span>
    );
  };

  const isDownloading = (loc: Location, type: string) => downloading === `${loc.id}-${type}`;

  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      <Navigation />
      <div className="px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-zinc-100">
              Uploaded Image Locations
            </h1>
            <p className="text-zinc-500 mt-1 text-sm">
              Manage and download your photo collections
            </p>
          </div>

          {/* Tier Info Banner */}
          {tierInfo && (
            <div className={`mb-6 p-4 rounded-xl flex items-center justify-between ${
              tierInfo.unlimited
                ? 'bg-emerald-500/10 border border-emerald-500/20'
                : 'bg-amber-500/10 border border-amber-500/20'
            }`}>
              <div>
                <span className="text-sm text-zinc-400">Your plan: </span>
                <span className={`font-semibold capitalize ${tierInfo.unlimited ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {tierInfo.tier}
                </span>
                <span className="text-sm text-zinc-500 ml-2">
                  {tierInfo.unlimited ? (
                    '(Unlimited downloads per location)'
                  ) : (
                    `(${tierInfo.limit} download${tierInfo.limit !== 1 ? 's' : ''} per location)`
                  )}
                </span>
              </div>
              {!tierInfo.unlimited && (
                <Link
                  to="/subscribe"
                  className="btn-primary px-4 py-2 rounded-lg text-sm"
                >
                  Upgrade Plan
                </Link>
              )}
            </div>
          )}

          {error && (
            <div className="alert-error mb-6">{error}</div>
          )}

          {/* Table */}
          <div className="card-dark rounded-xl overflow-hidden">
            <table className="table-dark">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Remaining</th>
                  <th>Download</th>
                </tr>
              </thead>
              <tbody>
                {locations.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8 text-zinc-500">
                      No image locations found. Upload some images to get started.
                    </td>
                  </tr>
                ) : (
                  locations.map((loc, i) => (
                    <tr
                      key={i}
                      className="clickable"
                      onClick={() => handleRowClick(loc.location_taken)}
                    >
                      <td className="font-medium">{loc.location_taken}</td>
                      <td>{getDownloadsBadge(loc)}</td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => handleDownload(loc, 'unstructured', e)}
                            disabled={isDownloading(loc, 'unstructured') || (loc.downloads_remaining === 0 && !loc.is_unlimited)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all disabled:cursor-not-allowed ${
                              loc.downloads_remaining === 0 && !loc.is_unlimited
                                ? 'bg-zinc-800 text-zinc-600'
                                : loc.is_final_download && !loc.is_unlimited
                                ? 'bg-rose-500/90 hover:bg-rose-500 text-white hover:shadow-lg hover:shadow-rose-500/25'
                                : 'bg-cyan-500/90 hover:bg-cyan-500 text-zinc-900 hover:shadow-lg hover:shadow-cyan-500/25'
                            }`}
                            title="Download all images in a flat structure"
                          >
                            {isDownloading(loc, 'unstructured') ? 'Downloading...' : 'Flat ZIP'}
                          </button>
                          <button
                            onClick={(e) => handleDownload(loc, 'structured', e)}
                            disabled={isDownloading(loc, 'structured') || (loc.downloads_remaining === 0 && !loc.is_unlimited)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all disabled:cursor-not-allowed ${
                              loc.downloads_remaining === 0 && !loc.is_unlimited
                                ? 'bg-zinc-800 text-zinc-600'
                                : loc.is_final_download && !loc.is_unlimited
                                ? 'bg-rose-500/90 hover:bg-rose-500 text-white hover:shadow-lg hover:shadow-rose-500/25'
                                : 'btn-primary'
                            }`}
                            title="Download images organized in folders by tag"
                          >
                            {isDownloading(loc, 'structured') ? 'Downloading...' : 'Folders ZIP'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadsPage;
